import { createFileRoute, Link } from "@tanstack/react-router";
import { openExternal } from "@/lib/open-external";
import {
  ExternalLink,
  Heart,
  Shield,
  Users,
  HelpCircle,
  AlertTriangle,
  Globe,
  Sparkles,
  PenLine,
  Stethoscope,
  ArrowRight,
} from "lucide-react";


export const Route = createFileRoute("/crisis")({
  head: () => ({
    meta: [
      { title: "Crisis Support — Recovery Trauma™" },
      {
        name: "description",
        content:
          "If you are in crisis, find trusted global support. Recovery Trauma™ is educational — please contact your local emergency services for urgent help.",
      },
      { property: "og:title", content: "Crisis Support — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Trusted global crisis support, bereavement help and mental health organisations. You are not alone.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/crisis" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/crisis" }],
  }),
  component: CrisisPage,
});

type Org = {
  name: string;
  description: string;
  url: string;
  cta?: string;
};

const MENTAL_HEALTH_ORGS: Org[] = [
  {
    name: "Mind UK",
    description: "Mental health information and support.",
    url: "https://www.mind.org.uk/",
  },
  {
    name: "FindAHelpline",
    description:
      "Worldwide directory of crisis and emotional support services.",
    url: "https://findahelpline.com/",
  },
  {
    name: "Suicide&Co",
    description: "Support after suicide bereavement.",
    url: "https://www.suicideandco.org/",
  },
  {
    name: "NAMI",
    description:
      "National Alliance on Mental Illness — education, resources and support.",
    url: "https://www.nami.org/",
  },
  {
    name: "Mental Health America",
    description: "Mental health information, screening tools and resources.",
    url: "https://mhanational.org/",
  },
  {
    name: "Befrienders Worldwide",
    description:
      "International suicide prevention and emotional support network.",
    url: "https://www.befrienders.org/",
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "🌍 I live outside the UK. Can I still use Recovery Trauma™?",
    a: "Absolutely. Recovery Trauma™ is available to people around the world. If you ever need urgent support, we encourage you to contact your local emergency services or use FindAHelpline to locate crisis resources in your country.",
  },
  {
    q: "💛 Is Recovery Trauma™ a crisis support service?",
    a: "Recovery Trauma™ is a wellbeing platform created to support your recovery journey through education, self-help tools, guided exercises, and community. While we hope it becomes a comforting place to return to, it isn't able to provide emergency or crisis support.",
  },
  {
    q: "🕊️ What if I'm in crisis or feel unsafe?",
    a: "Your safety comes first.\n\nIf you're in immediate danger or you're worried you may act on thoughts of harming yourself or someone else, please contact your local emergency services or a trusted crisis service in your area straight away.\n\nIf you're unsure where to turn, FindAHelpline can help you find support wherever you are in the world.",
  },
  {
    q: "🌱 Can Recovery Trauma™ replace therapy?",
    a: "Recovery Trauma™ is designed to complement, not replace, professional mental health care. Many people use the platform alongside therapy, while others use it as part of their personal wellbeing routine.",
  },
  {
    q: "👥 How do I find a therapist?",
    a: "You can browse our trauma-informed therapist directory to discover qualified professionals who match your location, language, and areas of expertise. Every therapist is independently reviewed before appearing in the directory.",
  },
  {
    q: "🔒 Is my journal private?",
    a: "Yes. Your Recovery Journal, Dream Journal, Shadow Work entries, and personal reflections remain private and are never shared with the community unless you choose to post something yourself.",
  },
];


function SectionCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-3xl border border-gold/25 bg-cream/40 p-6 shadow-[0_4px_20px_-12px_rgba(184,134,11,0.25)] backdrop-blur-sm sm:p-8 ${className}`}
    >
      {children}
    </section>
  );
}

function SectionHeading({
  emoji,
  title,
  subtitle,
}: {
  emoji: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <header className="mb-5">
      <h2 className="font-display text-2xl tracking-tight text-foreground sm:text-3xl">
        <span aria-hidden className="me-2">
          {emoji}
        </span>
        {title}
      </h2>
      {subtitle && (
        <p className="mt-2 text-sm leading-relaxed text-foreground/75 sm:text-base">
          {subtitle}
        </p>
      )}
    </header>
  );
}

function VisitButton({
  href,
  label = "Visit Website",
  ariaLabel,
}: {
  href: string;
  label?: string;
  ariaLabel?: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => openExternal(href, e)}
      aria-label={ariaLabel ?? label}
      className="inline-flex items-center gap-2 rounded-full border border-[var(--gold-deep)]/60 bg-background/80 px-4 py-2 text-sm font-medium text-[var(--gold-deep)] transition-all hover:-translate-y-0.5 hover:border-[var(--gold-deep)] hover:shadow-[0_8px_20px_-8px_rgba(184,134,11,0.5)]"
    >
      {label}
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

function OrgCard({
  name,
  description,
  url,
  cta,
  icon,
}: Org & { icon?: React.ReactNode }) {
  return (
    <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-gold/20 bg-background/70 p-5 transition-all hover:border-[var(--gold-deep)]/50 hover:shadow-[0_8px_24px_-12px_rgba(184,134,11,0.35)]">
      <div>
        <div className="mb-2 flex items-center gap-2">
          {icon ?? (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--gold-deep)]/10 text-[var(--gold-deep)]">
              <Heart className="h-4 w-4" />
            </span>
          )}
          <h3 className="font-display text-lg text-foreground">{name}</h3>
        </div>
        <p className="text-sm leading-relaxed text-foreground/75">
          {description}
        </p>
        <p className="mt-2 break-all text-xs text-foreground/55">🌐 {url}</p>
      </div>
      <VisitButton
        href={url}
        label={cta ?? `Visit ${name}`}
        ariaLabel={`Visit ${name} website (opens in a new tab)`}
      />
    </article>
  );
}

function CrisisPage() {
  return (
    <main className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
      <div className="space-y-6 sm:space-y-8">
        {/* Immediate support */}
        <SectionCard className="border-[var(--gold-deep)]/40 bg-gradient-to-br from-cream/70 via-background to-cream/40">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--gold-deep)]/15 text-xl"
            >
              💛
            </span>
            <div>
              <h1 className="font-display text-2xl tracking-tight text-foreground sm:text-3xl">
                Need Immediate Support?
              </h1>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground/80 sm:text-base">
                <p>
                  If you are experiencing a mental health crisis, thoughts of
                  suicide, self-harm, or feel unable to keep yourself safe,
                  please contact your local emergency services immediately or
                  use a trusted crisis support service.
                </p>
                <p>
                  Recovery Trauma™ is an educational wellbeing platform and
                  cannot provide emergency or crisis intervention.
                </p>
                <p className="font-medium text-[var(--gold-deep)]">
                  You deserve support. Reaching out is a sign of strength.
                </p>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Find Crisis Support Worldwide */}
        <SectionCard className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute end-4 top-4 text-xl opacity-60 sm:text-2xl">💛</div>
          <div aria-hidden className="pointer-events-none absolute bottom-4 start-6 text-lg opacity-50">💛</div>
          <SectionHeading
            emoji="🌍"
            title="Find Crisis Support Worldwide"
            subtitle="Recovery Trauma™ recommends FindAHelpline, a trusted global directory that helps people find crisis services in over 150 countries."
          />
          <OrgCard
            name="FindAHelpline"
            description="Worldwide directory of suicide prevention, emotional support and crisis services."
            url="https://findahelpline.com/"
            cta="Find Support in Your Country"
            icon={
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--gold-deep)]/10 text-[var(--gold-deep)]">
                <Globe className="h-4 w-4" />
              </span>
            }
          />
        </SectionCard>


        {/* Find a Therapist */}
        <SectionCard>
          <SectionHeading
            emoji="👩‍⚕️"
            title="Find a Therapist"
            subtitle="Looking for ongoing professional support? Browse our verified Recovery Trauma™ therapist directory by country and speciality."
          />
          <div className="flex flex-col items-start gap-4 rounded-2xl border border-gold/20 bg-background/70 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--gold-deep)]/10 text-[var(--gold-deep)]">
                <Stethoscope className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-lg text-foreground">
                  Verified Therapist Directory
                </h3>
                <p className="text-sm leading-relaxed text-foreground/75">
                  Trauma-informed therapists worldwide — online and in-person.
                </p>
              </div>
            </div>
            <Link
              to="/therapists"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--gold-deep)]/60 bg-background/80 px-4 py-2 text-sm font-medium text-[var(--gold-deep)] transition-all hover:-translate-y-0.5 hover:border-[var(--gold-deep)] hover:shadow-[0_8px_20px_-8px_rgba(184,134,11,0.5)]"
            >
              Find a Therapist
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </SectionCard>

        {/* Bereavement */}
        <SectionCard>

          <SectionHeading
            emoji="💙"
            title="Bereavement Support"
            subtitle="If you have lost someone to suicide, these organisations provide specialist bereavement support."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <OrgCard
              name="Suicide&Co"
              description="Professional counselling, resources and support for people bereaved by suicide."
              url="https://www.suicideandco.org/"
              icon={
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--gold-deep)]/10 text-[var(--gold-deep)]">
                  <Sparkles className="h-4 w-4" />
                </span>
              }
            />
            <OrgCard
              name="Words Unspoken"
              description="A safe place where people can write letters to loved ones they have lost."
              url="https://www.suicideandco.org/all-letters"
              icon={
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--gold-deep)]/10 text-[var(--gold-deep)]">
                  <PenLine className="h-4 w-4" />
                </span>
              }
            />
          </div>
        </SectionCard>

        {/* Trusted Mental Health Organisations */}
        <SectionCard>
          <SectionHeading
            emoji="🌱"
            title="Trusted Mental Health Organisations"
            subtitle="Independent organisations providing emotional support, information and resources."
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MENTAL_HEALTH_ORGS.map((org) => (
              <OrgCard key={org.name} {...org} />
            ))}
          </div>
        </SectionCard>

        {/* Supporting Someone Else */}
        <SectionCard>
          <SectionHeading
            emoji="❤️"
            title="Supporting Someone Else"
            subtitle="If you are worried about someone you care about:"
          />
          <ul className="space-y-3 text-sm leading-relaxed text-foreground/80 sm:text-base">
            {[
              "Stay with them if they are in immediate danger.",
              "Encourage them to contact emergency services or a trusted crisis service.",
              "Listen without judgement.",
              "Help them contact a trusted family member, friend, therapist or healthcare professional.",
              "If there is an immediate risk to life, call your local emergency services.",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span
                  aria-hidden
                  className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        {/* FAQ */}
        <SectionCard>
          <SectionHeading emoji="❓" title="Frequently Asked Questions" />
          <div className="space-y-4">
            {FAQS.map(({ q, a }) => (
              <details
                key={q}
                className="group rounded-2xl border border-gold/20 bg-background/70 p-4 open:border-[var(--gold-deep)]/40"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-medium text-foreground">
                  <span className="flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 text-[var(--gold-deep)]" />
                    {q}
                  </span>
                  <span
                    aria-hidden
                    className="text-[var(--gold-deep)] transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground/75">
                  {a}
                </p>
              </details>
            ))}
          </div>
        </SectionCard>

        {/* Disclaimer */}
        <SectionCard className="border-[var(--gold-deep)]/30 bg-background/60">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--gold-deep)]/10 text-[var(--gold-deep)]">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-xl text-foreground sm:text-2xl">
                Recovery Trauma™ Disclaimer
              </h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground/75">
                <p>
                  Recovery Trauma™ provides educational resources, wellbeing
                  tools, therapist discovery and community support.
                </p>
                <p>
                  We do not provide crisis intervention, emergency assistance,
                  medical advice, diagnosis or treatment.
                </p>
                <p>
                  If you believe you are in immediate danger or may harm
                  yourself or someone else, please contact your local emergency
                  services immediately or visit{" "}
                  <a
                    href="https://findahelpline.com/"
                    onClick={(e) => openExternal("https://findahelpline.com/", e)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--gold-deep)] underline underline-offset-2"
                  >
                    findahelpline.com
                  </a>{" "}
                  to locate the appropriate crisis support service in your
                  country.
                </p>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* You are not alone */}
        <section className="relative overflow-hidden rounded-3xl border border-[var(--gold-deep)]/40 bg-gradient-to-br from-cream/80 via-background to-cream/50 p-8 text-center shadow-[0_10px_40px_-20px_rgba(184,134,11,0.45)] sm:p-12">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,color-mix(in_oklab,var(--gold-deep)_22%,transparent),transparent_65%)]"
          />
          <div aria-hidden className="pointer-events-none absolute start-6 top-6 text-2xl opacity-70 sm:text-3xl">💛</div>
          <div aria-hidden className="pointer-events-none absolute end-6 top-10 text-xl opacity-60 sm:text-2xl">💛</div>
          <div aria-hidden className="pointer-events-none absolute bottom-8 start-10 text-xl opacity-60">💛</div>
          <div aria-hidden className="pointer-events-none absolute bottom-6 end-8 text-2xl opacity-70 sm:text-3xl">💛</div>

          <div className="relative">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--gold-deep)]/15 text-3xl">
              💛
            </div>
            <h2 className="font-display text-2xl tracking-tight text-foreground sm:text-3xl">
              You Are Not Alone
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-foreground/80 sm:text-base">
              Whatever you are carrying right now — grief, fear, exhaustion or
              pain — you are not walking through it alone. Reaching out is a
              quiet act of courage, and there are people ready to listen.
            </p>
            <p className="mx-auto mt-3 max-w-xl text-sm font-medium text-[var(--gold-deep)] sm:text-base">
              You matter. Your story matters. And there is a path forward.
            </p>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-foreground/55">
              With love, Recovery Trauma™
            </p>
          </div>
        </section>


        {/* Footer micro-credits (kept minimal, supportive) */}
        <div className="flex items-center justify-center gap-2 pt-2 text-xs text-foreground/55">
          <Shield className="h-3.5 w-3.5" />
          <span>Curated with care by Recovery Trauma™</span>
          <Users className="h-3.5 w-3.5" />
        </div>
      </div>
    </main>
  );
}
